import { type ComponentType, type FunctionComponent } from "react";

import { type HistoryActionKey } from "utilities/historyState/actionWithHistory.utility";

import { type SavedViewsAndFiltersSettingsState } from "entity/filters/SavedViewsAndFiltersList/SavedViewsAndFiltersCard/SavedViewsAndFiltersCard.api.types";
import { useSavedViewsSettingsState } from "entity/filters/SavedViewsAndFiltersList/useSavedViewsSettingsState";

/**
 * Props injected by {@link withSavedViewsSettingsState}. Class-component list
 * pages add this to their props interface and read/write the panel state through
 * it instead of owning a `gridSettingsState` field.
 */
export interface ISavedViewsSettingsStateProps {
    settingsState: SavedViewsAndFiltersSettingsState;
    setSettingsState: (next: SavedViewsAndFiltersSettingsState) => void;
}

/**
 * Injects {@link useSavedViewsSettingsState} as props so class components can use
 * the history-backed settings panel state they can't read from a hook directly.
 */
export function withSavedViewsSettingsState<P extends ISavedViewsSettingsStateProps>(
    WrappedComponent: ComponentType<P>,
    actionKey: HistoryActionKey,
): FunctionComponent<Omit<P, keyof ISavedViewsSettingsStateProps>> {
    const Bridge: FunctionComponent<Omit<P, keyof ISavedViewsSettingsStateProps>> = (props) => {
        const settings = useSavedViewsSettingsState(actionKey);
        return <WrappedComponent {...(props as P)} {...settings} />;
    };

    const wrappedName = WrappedComponent.displayName ?? WrappedComponent.name ?? "Component";
    Bridge.displayName = `withSavedViewsSettingsState(${wrappedName})`;

    return Bridge;
}
